import type { StatefulRoom } from "@/lib/sendbird-calls";
import { useState, useEffect } from "react";
import { MediaContent } from "@/components/group-call/media-content.tsx";
import { Mic, MicOff, PhoneOff, Settings, Video, VideoOff } from "lucide-react";
import { DeviceSettings } from "@/components/group-call/device-settings.tsx";
import { CallDisconnectConfirmationModal } from "@/components/group-call/call-disconnect-confirmation-modal.tsx";
import { useActiveCall } from "@/lib/api/calls.ts";
import { Button } from "@somethings-ui/web";

export const GroupCall = ({ room }: { room: StatefulRoom }) => {
  const { data: activeCall } = useActiveCall();

  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [
    showCallDisconnectConfirmationModal,
    setShowCallDisconnectConfirmationModal,
  ] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [permissionPrompt, setPermissionPrompt] = useState(false);

  const { localParticipant } = room;

  useEffect(() => {
    // Check permissions for camera and microphone
    async function checkPermissions() {
      if (navigator.permissions) {
        console.log("navigator.permissions", navigator.permissions);
        try {
          const micPerm = await navigator.permissions.query({
            name: "microphone" as PermissionName,
          });
          const camPerm = await navigator.permissions.query({
            name: "camera" as PermissionName,
          });
          console.log("micPerm", micPerm);
          console.log("camPerm", camPerm);
          if (micPerm.state === "denied" || camPerm.state === "denied") {
            setPermissionDenied(true);
            setPermissionPrompt(false);
          } else if (micPerm.state === "prompt" || camPerm.state === "prompt") {
            setPermissionPrompt(true);
            setPermissionDenied(false);
          }
        } catch (e) {
          console.log("e", e);
          // Permissions API not supported or failed, do nothing
        }
      }
    }
    checkPermissions();
    // Only run on mount
  }, []);

  // Handler to request permissions
  const handleRequestPermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      console.log(
        "navigator.mediaDevices.getUserMedia",
        navigator.mediaDevices,
      );
      // If successful, re-check permissions
      setPermissionPrompt(false);
      setPermissionDenied(false);
    } catch (e) {
      console.log("navigator.mediaDevices.getUserMedia error", e);
      setPermissionDenied(true);
      setPermissionPrompt(false);
    }
  };

  return (
    <div className="h-screen-nav relative flex bg-gray-800">
      {permissionPrompt && (
        <div className="absolute left-0 top-0 z-[200] flex w-full flex-row items-center justify-between bg-blue-200 px-4 py-3 text-center font-semibold text-black shadow-lg">
          <span>
            Camera and Microphone permission is required. Please grant access to
            join the call.
          </span>
          <Button
            onClick={handleRequestPermissions}
            variant="destructive"
            size="sm"
          >
            Request Permissions
          </Button>
        </div>
      )}
      {permissionDenied && (
        <div className="absolute left-0 top-0 z-[200] w-full bg-red-600 py-3 text-center font-semibold text-white shadow-lg">
          Camera or Microphone permission denied. Please allow access in your
          browser settings to join the call.
        </div>
      )}
      <div className="flex h-full w-full flex-col justify-between">
        <MediaContent room={room} />

        <div className="bg-neutral-navy-800 z-10 flex h-20 items-center justify-between px-4">
          <div className="h-12 w-12 p-3"></div>
          <div className="flex h-full items-center gap-4">
            <button
              className="h-12 w-12 rounded-full bg-gray-600 p-3"
              onClick={() => {
                if (localParticipant.isAudioEnabled) {
                  localParticipant.muteMicrophone();
                } else {
                  localParticipant.unmuteMicrophone();
                }
              }}
            >
              {!localParticipant.isAudioEnabled ? (
                <MicOff className="text-white" />
              ) : (
                <Mic className="text-white" />
              )}
            </button>
            <button
              className="h-12 w-12 rounded-full bg-gray-600 p-3"
              onClick={() => {
                if (localParticipant.isVideoEnabled) {
                  localParticipant.stopVideo();
                } else {
                  localParticipant.startVideo();
                }
              }}
            >
              {!localParticipant.isVideoEnabled ? (
                <VideoOff className="text-white" />
              ) : (
                <Video className="text-white" />
              )}
            </button>
            <button
              className="h-12 w-12 rounded-full bg-red-500 p-3"
              onClick={() => {
                setShowCallDisconnectConfirmationModal(true);
              }}
            >
              <PhoneOff className="text-white" />
            </button>
          </div>
          <button
            className="h-12 w-12 rounded-full bg-gray-600 p-3"
            onClick={() => {
              setShowDeviceSettings(true);
            }}
          >
            <Settings className="text-white" />
          </button>
        </div>
        <DeviceSettings
          open={showDeviceSettings}
          onClose={() => setShowDeviceSettings(false)}
        />
        <CallDisconnectConfirmationModal
          room={room}
          callId={activeCall?.id as string} // We can safely assume that activeCall is not null here
          open={showCallDisconnectConfirmationModal}
          onClose={() => setShowCallDisconnectConfirmationModal(false)}
        />
      </div>
    </div>
  );
};
